/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Tools epics
 */

import { Injectable } from '@angular/core';
import { combineEpics, Epic, StateObservable } from 'redux-observable';
import { Observable, observableEMPTY, observableOf, rx } from '@shared/rx';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { get, includes, isEmpty } from 'lodash';

import {
  APPLICATION_STARTUP,
  FETCH_API_INFO,
  FETCH_COMPONENT_INFO,
  SEND_HTTP_REQUEST,
  SessionActions,
  ToolsActions,
  UiActions,
  UI_CLEAR_SCHEMA_CACHE,
} from '../../actions';
import { ExpiringStorageService, ToolsService } from '@shared/services';
import { IAction, IApiAction, IEpicsService } from '@shared/types';
import { IAppState } from '@app/store';
import {
  generateId,
  isActionPostfixError,
  isActionPostfixSuccess,
} from '@shared/utils/common-utils';
import { ACTION_FOR_LISTENING } from '../../actions/base/listening.action';

@Injectable()
export class ToolsEpics implements IEpicsService {
  // eslint-disable-next-line max-params
  public constructor(
    public uiActions: UiActions,
    public toolsActions: ToolsActions,
    public http: HttpClient,
    public toolsService: ToolsService,
    public expiringStorage: ExpiringStorageService,
  ) {}

  public getEpic(): Epic<any, any> {
    return combineEpics(
      this.sendHttpRequest.bind(this),
      this.fetchInfo.bind(this),
      this.fetchComponentInfo.bind(this),
      this.startUp.bind(this),
      this.clearSchemaCache.bind(this),
      this.handleActionStatus.bind(this),
    );
  }

  /**
   * Method for catching SEND_HTTP_REQUEST action type
   * and calling HTTP service method
   *
   * @param action$ - action Observable
   * @returns
   */
  private sendHttpRequest(action$: Observable<IAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [SEND_HTTP_REQUEST].includes(action.type)),
      rx.mergeMap((action: IAction) => {
        const {
          method = 'GET',
          url,
          body,
          // eslint-disable-next-line comma-dangle
          ...reqOptions
        } = get(action, 'payload.request', { url: '' });
        const hasBody = includes(
          ['POST', 'PUT', 'PATCH'],
          String(method).toUpperCase(),
        );
        const request = hasBody
          ? new HttpRequest(method, url, body, reqOptions)
          : new HttpRequest(method, url, reqOptions);

        const options = get(action, 'payload.options');

        return this.http.request(request).pipe(
          rx.map((response) =>
            this.toolsActions.sendHttpRequestSuccessAction(
              response,
              request,
              options,
            ),
          ),
          rx.catchError((errors) =>
            observableOf(
              this.toolsActions.sendHttpRequestErrorAction(
                errors,
                request,
                options,
              ),
            ),
          ),
        );
      }),
    );
  }

  private handleActionStatus(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        includes(ACTION_FOR_LISTENING, get(action, 'type', null)),
      ),
      rx.mergeMap((action: IApiAction) => {
        const actionType: string = action.type;
        const requestId: string = get(
          action,
          'payload.options.requestId',
          generateId(),
        );

        if (isActionPostfixError(actionType)) {
          return observableOf(
            this.toolsActions.addActionStatusError({
              requestId,
              trackedAction: action,
            }),
          );
        }

        if (isActionPostfixSuccess(actionType)) {
          return observableOf(
            this.toolsActions.removeActionStatus({
              requestId,
              trackedAction: action,
            }),
          );
        }

        return observableOf(
          this.toolsActions.addActionStatus({
            requestId,
            trackedAction: action,
          }),
        );
      }),
    );
  }

  private fetchInfo(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [FETCH_API_INFO].includes(action.type)),
      rx.mergeMap((action: IAction) => {
        return this.toolsService.getEnvironmentInfo().pipe(
          rx.map((servicePayload) =>
            this.toolsActions.fetchInfoSuccessAction(
              servicePayload,
              action.payload.options,
            ),
          ),
          rx.catchError((errors) =>
            observableOf(
              this.toolsActions.fetchInfoErrorAction(
                errors,
                action.payload.options,
              ),
            ),
          ),
        );
      }),
    );
  }

  private startUp(action$: Observable<IAction>): Observable<any> {
    const isExternalLogin = this.toolsService.isExternalLogin();

    return action$.pipe(
      rx.filter((action) => [APPLICATION_STARTUP].includes(action.type)),
      rx.mergeMap((action: IAction) => {
        return isExternalLogin
          ? observableEMPTY
          : observableOf(SessionActions.restoreTokensAction());
      }),
      rx.catchError((errors) =>
        observableOf(SessionActions.restoreTokensAction()),
      ),
    );
  }

  private fetchComponentInfo(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [FETCH_COMPONENT_INFO].includes(action.type)),
      rx.mergeMap(() => {
        const state: IAppState = store.value;
        const componentObj = get(state, 'tools.componentInfo.components', {});
        const componentSource = isEmpty(componentObj)
          ? this.toolsService.getComponentList()
          : observableOf(componentObj);

        return componentSource.pipe(
          rx.map((servicePayload) =>
            this.toolsActions.fetchComponentInfoSuccessAction(servicePayload),
          ),
          rx.catchError((errors) =>
            observableOf(
              this.toolsActions.fetchComponentInfoErrorAction(errors),
            ),
          ),
        );
      }),
    );
  }
  private clearSchemaCache(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [UI_CLEAR_SCHEMA_CACHE].includes(action.type)),
      rx.flatMap((): Promise<any> => {
        return this.expiringStorage.removeAllItemsWithPrefix('schema_');
      }),
      rx.switchMapTo(observableEMPTY),
    );
  }
}
